import { useState } from "react";
import * as yup from 'yup';
import { getLocalizedString } from "@icarius-localization/strings";

const useHandleForm = (data, createCallback, modifyCallback, invalidDataCallback, invalidNameCallback, isDuplicate, typeOfScoreNames, typeOfScoreCodes, invalidCodeCallback, invalidDetailsNamesCallback) => {

  const DETAILS_ERRORS = {
    "INCOMPLETE_FIELDS": getLocalizedString("verifyThatAllFieldsAreComplete"),
    "LESS_THAN_ONE": getLocalizedString("theMinimumNoteValueIs01"),
    "VALUES_DO_NOT_MATCH": getLocalizedString("checkThresholdsMatch"),
    "NAME_LENGTH_GREATER_THAN": getLocalizedString("noteNameCannotExceed60Characters"),
    "SCORE_LENGTH_GREATER_THAN": getLocalizedString("minAndMaxScoresCannotBeGreaterThan9999"),
    "MINIMUM_GREATER_THAN_MAXIMUM": getLocalizedString("minimumGreaterThanMaximum"),
  }

  const isCreate = !Boolean(data);

  const createInitialData = () => {
    const getInitialFieldData = (fieldName) => {
      if (fieldName === 'details') {
        return isCreate ? [] : data[fieldName];
      }

      return isCreate ? "" : data[fieldName];
    }

    const fieldNames = [
      "code",
      "name",
      "maxScore",
      "details",
    ];

    return fieldNames.reduce((initialData, fieldName) => {
      return ({
        ...initialData,
        [fieldName]: getInitialFieldData(fieldName)
      })
    }, {});
  }

  const [formData, setFormData] = useState(createInitialData());
  const {
    code,
    name,
    maxScore,
    details,
  } = formData;
  const detailsNameIsValid = () => {
    const counts = {};
    details.map(el => el.name).forEach(function (x) { counts[x] = (counts[x] || 0) + 1; });
    if (Object.keys(counts).length !== details.length) {
      return false;
    }

    return true;
  }
  const detailsAreValid = () => {
    if (details.length === 0) return false;
    const minimumValues = details.map(el => el.minimum);
    const maximumValues = details.map(el => el.maximum);

    if (minimumValues.length !== maximumValues.length) {
      return DETAILS_ERRORS.INCOMPLETE_FIELDS;
    }

    // Primero reviso si tengo un número par de repetidos
    for (let i = 0; i < details.length; i++) {
      const { name, minimum, maximum, color } = details[i];

      if (name.length > 60) {
        return DETAILS_ERRORS.NAME_LENGTH_GREATER_THAN;
      }
      // Verifico tener los campos necesarios
      if (!color || !minimum || !maximum || !name) {
        return DETAILS_ERRORS.INCOMPLETE_FIELDS;
      }

      if (parseFloat(minimum) > parseFloat(maximum)) {
        return DETAILS_ERRORS.MINIMUM_GREATER_THAN_MAXIMUM;
      }

      if (i === 0) {
        if (parseFloat(minimum) < 0.1) {
          return DETAILS_ERRORS.LESS_THAN_ONE;
        }
      }

      if (i !== (details.length - 1)) {
        if (parseFloat(maximum) > parseFloat(details[i + 1].minimum)) {
          return DETAILS_ERRORS.VALUES_DO_NOT_MATCH;
        }
      }
    }

    return true;
  }

  const dataIsValid = async () => {
    const schema = yup.object().shape({
      'code': yup.string().required().max(8),
      'name': yup.string().required().max(60),
      'maxScore': yup.string().required().max(6),
    });

    return await schema.isValid(formData).then((valid) => valid);
  }

  const nameIsValid = () => {
    if (!isCreate && !isDuplicate) return true;
    return !(typeOfScoreNames.includes(name));
  }

  const codeIsValid = () => {
    if (!isCreate && !isDuplicate) return true;
    return !(typeOfScoreCodes.includes(code));
  }

  const submit = async () => {
    if (await dataIsValid()) {
      if (detailsAreValid() === true) {
        if (codeIsValid()) {
          if (nameIsValid()) {
            if (detailsNameIsValid()) {
              const dataToSend = {
                code,
                name,
                maxScore,
              }

              if (!isCreate) {
                dataToSend.code = data.code;
              }

              dataToSend.details = details.map((item) => {
                const newFormattedDetail = {
                  name: item.name,
                  minimum: parseFloat(item.minimum),
                  maximum: parseFloat(item.maximum),
                  points: parseInt(item.points),
                  color: item.color,
                };

                return newFormattedDetail;
              });

              isCreate ? createCallback(dataToSend) : modifyCallback(dataToSend);
              return true;
            } else {
              invalidDetailsNamesCallback();
              return false;
            }
          } else {
            invalidNameCallback();
            return false;
          }
        } else {
          invalidCodeCallback();
          return false;
        }
      } else {
        invalidDataCallback(detailsAreValid());
        return false;
      }
    } else {
      if (maxScore.length > 5) {
        invalidDataCallback(getLocalizedString("maxGradeCannotExceed9999"));
      } else if (code.length > 8) {
        invalidDataCallback(getLocalizedString("codeCannotExceed8Characters"));
      } else if (name.length > 60) {
        invalidDataCallback(getLocalizedString("nameExceeds60Characters"));
      }
      return false;
    }
  }

  const createItem = () => {
    //agregar el objeto default a details
    const newEmptyDetail = {
      name: "",
      minimum: "",
      maximum: "",
      color: "",
      points: "",
    }

    setFormValue([...details, newEmptyDetail], 'details');
  }

  const deleteItem = (index) => {
    const newDetails = [...details];
    newDetails.splice(index, 1);
    setFormValue(newDetails, 'details');
  }

  const modifyItem = (index, value, fieldName) => {
    const updatedDetail = { ...details[index] };
    updatedDetail[fieldName] = value;

    const newDetails = [...details];
    newDetails.splice(index, 1, updatedDetail);
    setFormValue(newDetails, 'details');
  }


  const setFormValue = (value, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: value,
    })
  }

  return {
    isCreate,
    formData,
    setFormValue,
    createItem,
    deleteItem,
    modifyItem,
    submit,
  };
}

export default useHandleForm;
